import { NAVIGATION_STORAGE_KEYS } from "./storage";

export const SIDEBAR_SCROLL_STORAGE_KEY = NAVIGATION_STORAGE_KEYS.SCROLL_TOP;

const RESTORE_RETRY_DELAYS_MS = [50, 250, 500];
const RESTORE_STABILITY_MS = 320;
const RESTORE_POLL_MS = 400;
const RESTORE_POLL_MAX_MS = 8000;

export function readStoredSidebarScrollTop() {
    if (typeof window === "undefined") {
        return null;
    }

    try {
        const rawValue = window.localStorage.getItem(SIDEBAR_SCROLL_STORAGE_KEY);
        if (rawValue == null || rawValue === "") {
            return null;
        }

        const value = Number(rawValue);
        if (!Number.isFinite(value) || value < 0) {
            return null;
        }

        return Math.round(value);
    } catch {
        return null;
    }
}

export function writeStoredSidebarScrollTop(value) {
    if (typeof window === "undefined" || !Number.isFinite(value) || value < 0) {
        return;
    }

    try {
        window.localStorage.setItem(SIDEBAR_SCROLL_STORAGE_KEY, String(Math.round(value)));
    } catch {
        // 忽略 localStorage 异常，避免影响主流程
    }
}

export function getSidebarScrollMax(element) {
    return Math.max(0, element.scrollHeight - element.clientHeight);
}

export function shouldCompleteSidebarScrollRestore(stored, maxScroll) {
    return stored == null || stored === 0 || maxScroll >= stored;
}

export function shouldDeferSidebarScrollPersist({ restoreLocked, stored, scrollTop, maxScroll }) {
    if (restoreLocked || stored == null || stored === 0) {
        return false;
    }

    if (maxScroll <= 0 && scrollTop <= 0) {
        return true;
    }

    const restoredTop = Math.min(stored, maxScroll);
    return maxScroll < stored && Math.abs(scrollTop - restoredTop) <= 1;
}

export function persistSidebarScrollTop(element) {
    if (!element) {
        return;
    }

    const maxScroll = getSidebarScrollMax(element);
    if (maxScroll <= 0 && element.scrollTop <= 0) {
        return;
    }

    writeStoredSidebarScrollTop(element.scrollTop);
}

export function restoreSidebarScrollTop(element) {
    if (!element) {
        return;
    }

    const stored = readStoredSidebarScrollTop();
    if (stored == null) {
        return;
    }

    element.scrollTop = Math.min(stored, getSidebarScrollMax(element));
}

export function bindSidebarScrollPersistence(element, { shouldPersist } = {}) {
    if (!element || typeof window === "undefined") {
        return () => {};
    }

    const enabled = () => (typeof shouldPersist === "function" ? shouldPersist() : shouldPersist !== false);
    let restoreLocked = false;
    let applyingRestore = false;
    let lastRestoreMaxScroll = Number.NaN;
    let resizeObserver = null;
    let mutationObserver = null;
    let stabilityTimer = 0;
    let pollTimer = 0;
    let pollStopTimer = 0;
    let firstFrame = 0;
    let secondFrame = 0;
    let retryTimers = [];

    const stopRetrying = () => {
        window.clearTimeout(stabilityTimer);
        retryTimers.forEach((timer) => window.clearTimeout(timer));
        retryTimers = [];
    };

    const stopRestoring = () => {
        restoreLocked = true;
        stopRetrying();
        window.clearInterval(pollTimer);
        window.clearTimeout(pollStopTimer);
        window.cancelAnimationFrame(firstFrame);
        window.cancelAnimationFrame(secondFrame);
        resizeObserver?.disconnect();
        mutationObserver?.disconnect();
    };

    const restore = () => {
        if (!enabled() || restoreLocked) {
            return;
        }

        const maxScroll = getSidebarScrollMax(element);
        if (maxScroll === lastRestoreMaxScroll) {
            return;
        }

        lastRestoreMaxScroll = maxScroll;
        applyingRestore = true;
        try {
            restoreSidebarScrollTop(element);
        } finally {
            applyingRestore = false;
        }

        const stored = readStoredSidebarScrollTop();
        if (shouldCompleteSidebarScrollRestore(stored, maxScroll)) {
            stopRestoring();
            return;
        }

        window.clearTimeout(stabilityTimer);
        stabilityTimer = window.setTimeout(stopRetrying, RESTORE_STABILITY_MS);
    };

    const persist = () => {
        if (!enabled() || applyingRestore) {
            return;
        }

        const stored = readStoredSidebarScrollTop();
        const maxScroll = getSidebarScrollMax(element);
        if (maxScroll <= 0 && element.scrollTop <= 0) {
            return;
        }

        if (shouldDeferSidebarScrollPersist({ restoreLocked, stored, scrollTop: element.scrollTop, maxScroll })) {
            return;
        }

        stopRestoring();
        persistSidebarScrollTop(element);
    };

    restore();

    firstFrame = window.requestAnimationFrame(() => {
        restore();
        secondFrame = window.requestAnimationFrame(restore);
    });
    if (!restoreLocked) {
        retryTimers = RESTORE_RETRY_DELAYS_MS.map((delay) => window.setTimeout(restore, delay));
        pollTimer = window.setInterval(restore, RESTORE_POLL_MS);
        pollStopTimer = window.setTimeout(() => {
            window.clearInterval(pollTimer);
        }, RESTORE_POLL_MAX_MS);
    }

    element.addEventListener("scroll", persist, { passive: true });
    element.addEventListener("click", persist, true);
    window.addEventListener("pagehide", persist);
    const onVisibilityChange = () => {
        if (document.visibilityState === "hidden") {
            persist();
        }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    if (element instanceof Element) {
        if (typeof ResizeObserver !== "undefined") {
            resizeObserver = new ResizeObserver(restore);
            resizeObserver.observe(element);
        }
        if (typeof MutationObserver !== "undefined") {
            mutationObserver = new MutationObserver(restore);
            mutationObserver.observe(element, { childList: true, subtree: true });
        }
    }

    return () => {
        stopRestoring();
        element.removeEventListener("scroll", persist);
        element.removeEventListener("click", persist, true);
        window.removeEventListener("pagehide", persist);
        document.removeEventListener("visibilitychange", onVisibilityChange);
    };
}
